// Risk Recent Activity Component
// Displays recent risk activities with scrollable list and infinite scroll

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../state/language/LanguageProvider';
import { RiskService } from '../../services/riskService';

const PAGE_SIZE = 15;
const SCROLL_THRESHOLD = 80;

interface RiskActivity {
  id: string;
  user_name: string;
  type: string;
  risk_title: string;
  risk_id?: string;
  timestamp: string;
}

export const RiskRecentActivity: React.FC = () => {
  const { t } = useLanguage();
  const [activities, setActivities] = useState<RiskActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const nextPageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const riskService = new RiskService();

  const loadPage = useCallback(async (pageNum: number, append: boolean) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const data = await riskService.getRecentActivity(pageNum, PAGE_SIZE);
      setActivities(prev => append ? [...prev, ...data] : data);
      setHasMore(data.length >= PAGE_SIZE);
    } catch (err) {
      console.error('Error loading recent activity:', err);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadPage(1, false);
  }, [loadPage]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD) {
      setLoadingMore(true);
      nextPageRef.current += 1;
      loadPage(nextPageRef.current, true);
    }
  }, [loadingMore, hasMore, loadPage]);

  return (
    <div className="border border-[var(--color-border)] rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-[var(--color-surface-text)] mb-4">
          {t('risk.dashboard.recentActivity')}
        </h3>
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flow-root max-h-80 overflow-y-auto"
        >
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-bg)]" />
            </div>
          ) : (
            <ul className="-mb-8">
              {activities.map((activity, activityIdx) => (
                <li key={activity.id}>
                  <div className="relative pb-8">
                    {activityIdx !== activities.length - 1 ? (
                      <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-[var(--color-border)]" aria-hidden="true" />
                    ) : null}
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-transparent">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-[var(--color-secondary-text)]">
                            <span className="text-[var(--color-surface-text)]">{activity.user_name}</span>{' '}
                            {activity.type.toLowerCase()} risk{' '}
                            <span className="text-[var(--color-surface-text)]">{activity.risk_title}</span>
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-[var(--color-secondary-text)]">
                          <time dateTime={activity.timestamp}>
                            {new Date(activity.timestamp).toLocaleDateString()}
                          </time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
              {activities.length === 0 && (
                <li>
                  <div className="text-center py-8 text-[var(--color-secondary-text)]">
                    <p>{t('risk.dashboard.noRecentActivity')}</p>
                  </div>
                </li>
              )}
              {loadingMore && (
                <li className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--color-accent-bg)]" />
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
